import React, { type ReactNode, useMemo } from 'react'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetSchedulingUsersQuery } from '../../../store/scheduling/scheduling'
import { useScheduleCrossClientMeetingsMutation } from '../../../store/crossClient/crossClient'
import { addToast } from '../../../store/toast/toastSlice'
import { type LocationRef, type MeetingMode } from '../../../models/Scheduling'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { bookingLocationFields } from '../../Scheduling/flows/form/scheduleHelpers'
import { Button } from '../../../components/Button/Button'
import { Spinner } from '../../../components/Spinner/Spinner'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import saveBarStyles from '../../Scheduling/flows/ScheduleSaveBar.module.scss'
import { type HostSelection } from './eventTypeCoverage'
import { type EligibilityInput, getInvitationRecipients, getInvitationSummary } from './invitationRecipients'
import { type SendBookingInvitationsOwners } from './useSendBookingInvitationsOwners'
import {
  SEND_INVITED,
  SEND_OWNER_TURNED_ON,
  SEND_PICK_EVENT_TYPE,
  SEND_FAILED_TITLE,
  SEND_FAILED_MESSAGE,
  SEND_LOAD_ERROR,
  SEND_SKIPPED,
  sendButtonLabel,
  sendSucceededToast,
  sendClientCount,
  sendNoEmail,
  sendNotFound,
  sendNoOwner,
  sendOwnerNotShared,
  sendOwnerWithout,
  sendSkippedReasons,
  sendUnsubscribed,
} from './sendBookingInvitationsCopy'
import styles from './SendPanel.module.scss'

interface CountLineProps {
  count: number
  text: string
  dotClassName: string
}

const CountLine: React.FC<CountLineProps> = ({ count, text, dotClassName }) => (
  <div className='d-flex align-items-center gap-2 zocks-text-white'>
    <i className={classNames('bi bi-circle-fill', styles.dot, dotClassName)} aria-hidden='true' />
    <span>
      <span className='bold'>{sendClientCount(count)}</span> {text}
    </span>
  </div>
)

interface SkippedReason {
  key: string
  count: number
  content: ReactNode
}

interface SkippedReasonsProps {
  reasons: SkippedReason[]
}

const SkippedReasons: React.FC<SkippedReasonsProps> = ({ reasons }) => (
  <div className='d-flex flex-column gap-1 text-start'>
    {reasons.map((reason) => (
      <div key={reason.key} className='fw-normal'>
        {reason.content}
      </div>
    ))}
  </div>
)

interface SendPanelProps {
  executionId: string
  clientIds: string[]
  owners: SendBookingInvitationsOwners
  hostSelection: HostSelection
  eventTypeId: string | null
  eventTypeName: string
  meetingMode: MeetingMode | null
  location: LocationRef | null
  barRef?: (element: HTMLDivElement | null) => void
}

export const SendPanel: React.FC<SendPanelProps> = ({
  executionId,
  clientIds,
  owners,
  hostSelection,
  eventTypeId,
  eventTypeName,
  meetingMode,
  location,
  barRef,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const { currentData: enabledUsers, isError: enabledUsersError } = useGetSchedulingUsersQuery(eventTypeId ? { accountId, eventTypeId } : skipToken)
  const [scheduleMeetings, { isLoading: isSending }] = useScheduleCrossClientMeetingsMutation()

  const isLoadError = owners.isError || enabledUsersError
  const eligibility = useMemo<EligibilityInput | null>(
    () =>
      enabledUsers && !owners.isLoading && !isLoadError
        ? {
            ownerIdByClientId: owners.ownerIdByClientId,
            unsubscribedClientIds: owners.unsubscribedClientIds,
            hostSelection,
            enabledUserIds: new Set(enabledUsers.map((user) => user.id)),
          }
        : null,
    [enabledUsers, owners.isLoading, isLoadError, owners.ownerIdByClientId, owners.unsubscribedClientIds, hostSelection],
  )
  const summary = useMemo(() => (eligibility ? getInvitationSummary(clientIds, owners.clients, eligibility) : null), [eligibility, clientIds, owners.clients])
  const isReady = Boolean(eventTypeId) && summary !== null
  const skippedCount = summary ? clientIds.length - summary.invitedCount : 0
  const isSendDisabled = !isReady || !summary || summary.invitedCount === 0 || isSending

  const send = async () => {
    if (!eventTypeId || !eligibility || !meetingMode) {
      return
    }
    const recipients = getInvitationRecipients(owners.clients, eligibility)
    if (recipients.length === 0) {
      return
    }
    const attributes = { flow: 'ccq', clientCount: recipients.length, eventTypeId, ownerMode: hostSelection.mode }
    await Sentry.startSpan({ name: 'ccq.send_booking_invitations', op: 'ui.action', attributes }, async () => {
      const result = await scheduleMeetings({
        accountId,
        executionId,
        email: { eventTypeId, ...bookingLocationFields(meetingMode, location) },
        targets: recipients.map((recipient) => ({ clientId: recipient.clientId, hostUserId: recipient.hostId })),
      })
      if ('error' in result) {
        dispatch(addToast({ variant: 'error', title: SEND_FAILED_TITLE, messages: [SEND_FAILED_MESSAGE] }))
        return
      }
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_INVITATION_SENT, eventDetails: attributes })
      dispatch(addToast({ variant: 'success', ...sendSucceededToast(recipients.length) }))
      navigate(`/cross-client/executions/${executionId}`)
    })
  }

  const reasons: SkippedReason[] = summary
    ? [
        { key: 'no-owner', count: summary.noOwnerCount, content: sendNoOwner(summary.noOwnerCount) },
        { key: 'owner-not-shared', count: summary.ownerNotSharedCount, content: sendOwnerNotShared(summary.ownerNotSharedCount) },
        {
          key: 'owner-off',
          count: summary.ownerNotEnabledCount,
          content: (
            <>
              {sendOwnerWithout(summary.ownerNotEnabledCount)} <span className='text-nowrap'>{eventTypeName}</span> {SEND_OWNER_TURNED_ON}
            </>
          ),
        },
        { key: 'no-email', count: summary.noEmailCount, content: sendNoEmail(summary.noEmailCount) },
        { key: 'unsubscribed', count: summary.unsubscribedCount, content: sendUnsubscribed(summary.unsubscribedCount) },
        { key: 'not-found', count: summary.notFoundCount, content: sendNotFound(summary.notFoundCount) },
      ].filter((reason) => reason.count > 0)
    : []

  const counts = (() => {
    if (!eventTypeId) {
      return <span className='zocks-text-white'>{SEND_PICK_EVENT_TYPE}</span>
    }
    if (isLoadError) {
      return <span className='zocks-text-white'>{SEND_LOAD_ERROR}</span>
    }
    if (!isReady || !summary) {
      return <Spinner size='sm' />
    }
    return (
      <div className='d-flex align-items-center flex-wrap column-gap-4 row-gap-1'>
        <CountLine count={summary.invitedCount} text={SEND_INVITED} dotClassName='zocks-text-success' />
        {skippedCount > 0 ? (
          <div className='d-flex align-items-center gap-2'>
            <CountLine count={skippedCount} text={SEND_SKIPPED} dotClassName='zocks-text-gray-400' />
            {reasons.length > 0 ? (
              <Tooltip className={styles.skippedTooltip} tooltipContent={<SkippedReasons reasons={reasons} />}>
                <button type='button' className='btn btn-link p-0 border-0 lh-1' aria-label={sendSkippedReasons(skippedCount)}>
                  <i className='bi bi-info-circle zocks-text-gray-400' aria-hidden='true' />
                </button>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  })()

  return (
    <div ref={barRef} className={classNames('position-sticky mt-auto mx-auto rounded-3 shadow', saveBarStyles.bar, styles.bar)}>
      <div className='zocks-bg-color-gray-900 p-3 rounded-3'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          {counts}
          <Button type='button' variant='primary' className='border-0 text-nowrap' disabled={isSendDisabled} onClick={() => void send()}>
            {isSending ? (
              <span className='me-1'>
                <Spinner size='sm' />
              </span>
            ) : (
              <i className='bi bi-send me-1' />
            )}
            {sendButtonLabel(isReady && summary ? summary.invitedCount : null)}
          </Button>
        </div>
      </div>
    </div>
  )
}
